// Waves & Matter Calculators helpers: wave speed (v=fλ), wave period (T=1/f),
// the Doppler effect, speed of sound in air, sound intensity level (decibels),
// Snell's law (refraction), standing wave / string harmonics, buoyancy
// (Archimedes' principle), hydrostatic pressure, Boyle's law, specific
// gravity, and Young's modulus (stress/strain).
//
// Distinct from lib/tools/chemistryCalculators.ts, which already has a
// general Ideal Gas Law (PV=nRT) and a plain Density calculator - this file
// deliberately does NOT duplicate those; "Boyle's Law" here is the
// state-1-to-state-2 P1V1=P2V2 relation (constant temperature, no moles
// involved), and "Specific Gravity" is a substance/water density *ratio*,
// a conceptually distinct tool from plain density.
import { t } from './i18n'

const SUPERSCRIPT_DIGITS: Record<string, string> = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻' }
function toSuperscript(text: string): string {
    return text.split('').map((c) => SUPERSCRIPT_DIGITS[c] || c).join('')
}
function formatScientific(n: number, unit: string): string {
    if (n === 0) return `0 ${unit}`
    const exp = Math.floor(Math.log10(Math.abs(n)))
    if (exp > -3 && exp < 7) return `${n.toFixed(4)} ${unit}`
    const mantissa = n / Math.pow(10, exp)
    return `${mantissa.toFixed(4)} × 10${toSuperscript(String(exp))} ${unit}`
}

const NO_SOLUTION_LABEL: Record<string, string> = {
    en: 'No solution', ru: 'Решения нет', lv: 'Nav risinājuma', pl: 'Brak rozwiązania',
    es: 'Sin solución', fr: 'Pas de solution', it: 'Nessuna soluzione', de: 'Keine Lösung',
}
const TOTAL_INTERNAL_REFLECTION_LABEL: Record<string, string> = {
    en: 'Total internal reflection (no refraction)', ru: 'Полное внутреннее отражение (преломления нет)',
    lv: 'Pilnīgā iekšējā atstarošanās (refrakcijas nav)', pl: 'Całkowite wewnętrzne odbicie (brak załamania)',
    es: 'Reflexión interna total (sin refracción)', fr: 'Réflexion interne totale (pas de réfraction)',
    it: 'Riflessione interna totale (nessuna rifrazione)', de: 'Totalreflexion (keine Brechung)',
}

// ============================================================
// 1. Wave Speed Calculator (v = f * lambda)
// ============================================================
export function waveSpeedCalculator(params: { solve_for: unknown; wave_speed_ms: unknown; frequency_hz: unknown; wavelength_m: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'wave_speed')
    const v = Number(params.wave_speed_ms) || 0
    const f = Number(params.frequency_hz) || 0
    const lambda = Number(params.wavelength_m) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'frequency') {
        if (lambda === 0) return { result: errText, steps: errText }
        const result = v / lambda
        return { result, steps: `f = v / λ = ${v} / ${lambda} = ${result.toFixed(4)} Hz` }
    }
    if (solveFor === 'wavelength') {
        if (f === 0) return { result: errText, steps: errText }
        const result = v / f
        return { result, steps: `λ = v / f = ${v} / ${f} = ${result.toFixed(4)} m` }
    }
    const result = f * lambda
    return { result, steps: `v = f × λ = ${f} × ${lambda} = ${result.toFixed(4)} m/s` }
}

// ============================================================
// 2. Wave Period Calculator (T = 1/f)
// ============================================================
export function wavePeriodCalculator(params: { solve_for: unknown; period_s: unknown; frequency_hz: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'period')
    const period = Number(params.period_s) || 0
    const f = Number(params.frequency_hz) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'frequency') {
        if (period === 0) return { result: errText, steps: errText }
        const result = 1 / period
        return { result, steps: `f = 1 / T = 1 / ${period} = ${result.toFixed(4)} Hz` }
    }
    if (f === 0) return { result: errText, steps: errText }
    const result = 1 / f
    return { result, steps: `T = 1 / f = 1 / ${f} = ${result.toFixed(4)} s` }
}

// ============================================================
// 3. Doppler Effect Calculator
// (f_o = f_s * (v_sound + v_observer) / (v_sound - v_source), signed
// velocities: positive v_observer = moving toward the source, positive
// v_source = moving toward the observer)
// ============================================================
export function dopplerEffectCalculator(params: { source_frequency_hz: unknown; sound_speed_ms: unknown; source_velocity_ms: unknown; observer_velocity_ms: unknown; language: unknown }): {
    observed_frequency_hz: number | string; steps: string
} {
    const fs = Math.max(Number(params.source_frequency_hz) || 0, 0)
    const vSound = Number(params.sound_speed_ms) || 343
    const vSource = Number(params.source_velocity_ms) || 0
    const vObserver = Number(params.observer_velocity_ms) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    const denominator = vSound - vSource
    if (denominator === 0) return { observed_frequency_hz: errText, steps: errText }

    const result = fs * (vSound + vObserver) / denominator
    const steps = `f_o = f_s × (v_sound + v_observer) / (v_sound - v_source) = ${fs} × (${vSound} + ${vObserver}) / (${vSound} - ${vSource}) = ${result.toFixed(4)} Hz`

    return { observed_frequency_hz: result, steps }
}

// ============================================================
// 4. Speed of Sound (in air) Calculator (v = 331.3 + 0.606T)
// ============================================================
export function speedOfSoundCalculator(params: { solve_for: unknown; temperature_c: unknown; speed_of_sound_ms: unknown; language: unknown }): {
    result: number; steps: string
} {
    const solveFor = String(params.solve_for || 'speed_of_sound')
    const t_c = Number(params.temperature_c) || 0
    const v = Number(params.speed_of_sound_ms) || 0

    if (solveFor === 'temperature') {
        const result = (v - 331.3) / 0.606
        return { result, steps: `T = (v - 331.3) / 0.606 = (${v} - 331.3) / 0.606 = ${result.toFixed(4)} °C` }
    }
    const result = 331.3 + 0.606 * t_c
    return { result, steps: `v = 331.3 + 0.606 × T = 331.3 + 0.606 × ${t_c} = ${result.toFixed(4)} m/s` }
}

// ============================================================
// 5. Decibel / Sound Intensity Level Calculator (beta = 10 log10(I/I0))
// ============================================================
const I0 = 1e-12 // threshold of hearing, W/m^2

export function decibelCalculator(params: { solve_for: unknown; intensity_wm2: unknown; decibels: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'decibels')
    const intensity = Number(params.intensity_wm2) || 0
    const db = Number(params.decibels) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'intensity') {
        const result = I0 * Math.pow(10, db / 10)
        return { result, steps: `I = I₀ × 10^(β/10) = ${formatScientific(I0, 'W/m²')} × 10^(${db}/10) = ${formatScientific(result, 'W/m²')}` }
    }
    if (intensity <= 0) return { result: errText, steps: errText }
    const result = 10 * Math.log10(intensity / I0)
    return { result, steps: `β = 10 × log₁₀(I / I₀) = 10 × log₁₀(${formatScientific(intensity, 'W/m²')} / ${formatScientific(I0, 'W/m²')}) = ${result.toFixed(4)} dB` }
}

// ============================================================
// 6. Snell's Law (Refraction) Calculator (n1 sin(theta1) = n2 sin(theta2))
// ============================================================
export function snellsLawCalculator(params: { solve_for: unknown; n1: unknown; angle1_deg: unknown; n2: unknown; angle2_deg: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'angle2')
    const n1 = Number(params.n1) || 0
    const a1 = Number(params.angle1_deg) || 0
    const n2 = Number(params.n2) || 0
    const a2 = Number(params.angle2_deg) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)
    const tirText = t(TOTAL_INTERNAL_REFLECTION_LABEL, params.language)

    if (solveFor === 'angle1') {
        if (n1 === 0) return { result: errText, steps: errText }
        const sinA1 = (n2 * Math.sin((a2 * Math.PI) / 180)) / n1
        if (sinA1 < -1 || sinA1 > 1) return { result: tirText, steps: tirText }
        const result = (Math.asin(sinA1) * 180) / Math.PI
        return { result, steps: `θ₁ = asin(n₂ × sin(θ₂) / n₁) = asin(${n2} × sin(${a2}°) / ${n1}) = ${result.toFixed(4)}°` }
    }
    if (solveFor === 'n1') {
        const sinA1 = Math.sin((a1 * Math.PI) / 180)
        if (sinA1 === 0) return { result: errText, steps: errText }
        const result = (n2 * Math.sin((a2 * Math.PI) / 180)) / sinA1
        return { result, steps: `n₁ = n₂ × sin(θ₂) / sin(θ₁) = ${n2} × sin(${a2}°) / sin(${a1}°) = ${result.toFixed(4)}` }
    }
    if (solveFor === 'n2') {
        const sinA2 = Math.sin((a2 * Math.PI) / 180)
        if (sinA2 === 0) return { result: errText, steps: errText }
        const result = (n1 * Math.sin((a1 * Math.PI) / 180)) / sinA2
        return { result, steps: `n₂ = n₁ × sin(θ₁) / sin(θ₂) = ${n1} × sin(${a1}°) / sin(${a2}°) = ${result.toFixed(4)}` }
    }
    if (n2 === 0) return { result: errText, steps: errText }
    const sinA2 = (n1 * Math.sin((a1 * Math.PI) / 180)) / n2
    if (sinA2 < -1 || sinA2 > 1) return { result: tirText, steps: tirText }
    const result = (Math.asin(sinA2) * 180) / Math.PI
    return { result, steps: `θ₂ = asin(n₁ × sin(θ₁) / n₂) = asin(${n1} × sin(${a1}°) / ${n2}) = ${result.toFixed(4)}°` }
}

// ============================================================
// 7. Standing Wave / String Harmonics Calculator (f_n = n*v / (2L))
// ============================================================
export function standingWaveCalculator(params: { harmonic_number: unknown; string_length_m: unknown; wave_speed_ms: unknown; language: unknown }): {
    harmonic_frequency_hz: number; wavelength_m: number; steps: string
} {
    const n = Math.max(Math.round(Number(params.harmonic_number) || 1), 1)
    const length = Math.max(Number(params.string_length_m) || 0, 0)
    const v = Math.max(Number(params.wave_speed_ms) || 0, 0)

    const wavelength = (2 * length) / n
    const frequency = length > 0 ? (n * v) / (2 * length) : 0

    const steps = [
        `λₙ = 2L / n = 2 × ${length} / ${n} = ${wavelength.toFixed(4)} m`,
        `fₙ = n × v / (2L) = ${n} × ${v} / (2 × ${length}) = ${frequency.toFixed(4)} Hz`,
    ].join('\n')

    return { harmonic_frequency_hz: frequency, wavelength_m: wavelength, steps }
}

// ============================================================
// 8. Buoyancy Force Calculator (F_b = rho_fluid * V * g)
// ============================================================
const STATUS_FLOATS: Record<string, string> = {
    en: 'Floats', ru: 'Плавает', lv: 'Peld', pl: 'Unosi się', es: 'Flota', fr: 'Flotte', it: 'Galleggia', de: 'Schwimmt',
}
const STATUS_SINKS: Record<string, string> = {
    en: 'Sinks', ru: 'Тонет', lv: 'Grimst', pl: 'Tonie', es: 'Se hunde', fr: 'Coule', it: 'Affonda', de: 'Sinkt',
}
const STATUS_NEUTRAL: Record<string, string> = {
    en: 'Neutral buoyancy', ru: 'Нейтральная плавучесть', lv: 'Neitrāls peldspējas stāvoklis', pl: 'Pływalność neutralna',
    es: 'Flotabilidad neutra', fr: 'Flottabilité neutre', it: 'Galleggiamento neutro', de: 'Neutraler Auftrieb',
}

export function buoyancyCalculator(params: { fluid_density_kgm3: unknown; volume_m3: unknown; object_mass_kg: unknown; gravity_ms2: unknown; language: unknown }): {
    buoyant_force_n: number; object_weight_n: number; status: string; steps: string
} {
    const rho = Math.max(Number(params.fluid_density_kgm3) || 0, 0)
    const volume = Math.max(Number(params.volume_m3) || 0, 0)
    const mass = Math.max(Number(params.object_mass_kg) || 0, 0)
    const g = Number(params.gravity_ms2) || 9.81

    const buoyantForce = rho * volume * g
    const weight = mass * g

    let status: string
    if (Math.abs(buoyantForce - weight) < 1e-6) status = t(STATUS_NEUTRAL, params.language)
    else if (buoyantForce > weight) status = t(STATUS_FLOATS, params.language)
    else status = t(STATUS_SINKS, params.language)

    const steps = [
        `F_b = ρ_fluid × V × g = ${rho} × ${volume} × ${g} = ${buoyantForce.toFixed(4)} N`,
        `Weight = m × g = ${mass} × ${g} = ${weight.toFixed(4)} N`,
        `F_b ${buoyantForce > weight ? '>' : buoyantForce < weight ? '<' : '='} Weight → ${status}`,
    ].join('\n')

    return { buoyant_force_n: buoyantForce, object_weight_n: weight, status, steps }
}

// ============================================================
// 9. Hydrostatic Pressure Calculator (P = P0 + rho*g*h)
// ============================================================
export function hydrostaticPressureCalculator(params: { fluid_density_kgm3: unknown; depth_m: unknown; gravity_ms2: unknown; atmospheric_pressure_pa: unknown; language: unknown }): {
    gauge_pressure_pa: number; absolute_pressure_pa: number; steps: string
} {
    const rho = Math.max(Number(params.fluid_density_kgm3) || 0, 0)
    const depth = Math.max(Number(params.depth_m) || 0, 0)
    const g = Number(params.gravity_ms2) || 9.81
    const atm = Number(params.atmospheric_pressure_pa) || 101325

    const gaugePressure = rho * g * depth
    const absolutePressure = atm + gaugePressure

    const steps = [
        `P_gauge = ρ × g × h = ${rho} × ${g} × ${depth} = ${gaugePressure.toFixed(4)} Pa`,
        `P_absolute = P₀ + P_gauge = ${atm} + ${gaugePressure.toFixed(4)} = ${absolutePressure.toFixed(4)} Pa`,
    ].join('\n')

    return { gauge_pressure_pa: gaugePressure, absolute_pressure_pa: absolutePressure, steps }
}

// ============================================================
// 10. Boyle's Law Calculator (P1V1 = P2V2)
// ============================================================
export function boylesLawCalculator(params: { solve_for: unknown; pressure1: unknown; volume1: unknown; pressure2: unknown; volume2: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'pressure2')
    const p1 = Number(params.pressure1) || 0
    const v1 = Number(params.volume1) || 0
    const p2 = Number(params.pressure2) || 0
    const v2 = Number(params.volume2) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'volume2') {
        if (p2 === 0) return { result: errText, steps: errText }
        const result = (p1 * v1) / p2
        return { result, steps: `V₂ = P₁V₁ / P₂ = (${p1} × ${v1}) / ${p2} = ${result.toFixed(4)}` }
    }
    if (solveFor === 'pressure1') {
        if (v1 === 0) return { result: errText, steps: errText }
        const result = (p2 * v2) / v1
        return { result, steps: `P₁ = P₂V₂ / V₁ = (${p2} × ${v2}) / ${v1} = ${result.toFixed(4)}` }
    }
    if (solveFor === 'volume1') {
        if (p1 === 0) return { result: errText, steps: errText }
        const result = (p2 * v2) / p1
        return { result, steps: `V₁ = P₂V₂ / P₁ = (${p2} × ${v2}) / ${p1} = ${result.toFixed(4)}` }
    }
    if (v2 === 0) return { result: errText, steps: errText }
    const result = (p1 * v1) / v2
    return { result, steps: `P₂ = P₁V₁ / V₂ = (${p1} × ${v1}) / ${v2} = ${result.toFixed(4)}` }
}

// ============================================================
// 11. Specific Gravity Calculator (SG = rho_substance / rho_reference)
// ============================================================
export function specificGravityCalculator(params: { solve_for: unknown; substance_density_kgm3: unknown; reference_density_kgm3: unknown; specific_gravity_value: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'specific_gravity')
    const substance = Number(params.substance_density_kgm3) || 0
    const reference = Number(params.reference_density_kgm3) || 1000
    const sg = Number(params.specific_gravity_value) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'substance_density') {
        const result = sg * reference
        return { result, steps: `ρ_substance = SG × ρ_reference = ${sg} × ${reference} = ${result.toFixed(4)} kg/m³` }
    }
    if (solveFor === 'reference_density') {
        if (sg === 0) return { result: errText, steps: errText }
        const result = substance / sg
        return { result, steps: `ρ_reference = ρ_substance / SG = ${substance} / ${sg} = ${result.toFixed(4)} kg/m³` }
    }
    if (reference === 0) return { result: errText, steps: errText }
    const result = substance / reference
    return { result, steps: `SG = ρ_substance / ρ_reference = ${substance} / ${reference} = ${result.toFixed(4)}` }
}

// ============================================================
// 12. Young's Modulus (Stress-Strain) Calculator
// ============================================================
export function youngsModulusCalculator(params: { force_n: unknown; cross_section_area_m2: unknown; original_length_m: unknown; extension_m: unknown; language: unknown }): {
    stress_pa: string; strain: number; youngs_modulus_pa: string; youngs_modulus_gpa: number; steps: string
} {
    const force = Number(params.force_n) || 0
    const area = Math.max(Number(params.cross_section_area_m2) || 0.0000001, 0.0000001)
    const length0 = Math.max(Number(params.original_length_m) || 0.0001, 0.0001)
    const extension = Number(params.extension_m) || 0

    const stress = force / area
    const strain = extension / length0
    const modulus = strain !== 0 ? stress / strain : 0
    const modulusGpa = modulus / 1e9

    const steps = [
        `Stress = F / A = ${force} / ${area} = ${formatScientific(stress, 'Pa')}`,
        `Strain = ΔL / L₀ = ${extension} / ${length0} = ${strain.toFixed(6)}`,
        `E = Stress / Strain = ${formatScientific(stress, 'Pa')} / ${strain.toFixed(6)} = ${formatScientific(modulus, 'Pa')} (${modulusGpa.toFixed(4)} GPa)`,
    ].join('\n')

    return { stress_pa: formatScientific(stress, 'Pa'), strain, youngs_modulus_pa: formatScientific(modulus, 'Pa'), youngs_modulus_gpa: modulusGpa, steps }
}
